/**
 * Batches list (§12.1, §40.5 — Task 2). The management overview: every batch with
 * its mode, derived status, per-order progress rollup, and created time. Clicking
 * a row opens the batch detail (resume / retry / void + document printing).
 */
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, type BatchSummary } from "../lib/api.js";
import { batchStatusTone, batchStatusLabel, modeLabel } from "../lib/batchStatus.js";
import { formatAge } from "../lib/age.js";
import { Badge, Icon, EmptyState, ErrorState, SkeletonRows, Button } from "../components/ui/index.js";

/** Compact progress: packed / total + a hint of label + exception counts. */
function BatchProgress({ b }: { b: BatchSummary }) {
  const c = b.counts;
  const labelled = (c.LABEL_CREATED ?? 0) + (c.PRINTED ?? 0);
  const failed = c.FAILED ?? 0;
  return (
    <span className="batch-progress">
      <span className="batch-progress__main tnum">
        {c.packed}/{b.orderCount} packed
      </span>
      {labelled > 0 && <span className="batch-progress__seg batch-progress__seg--label">{labelled} labelled</span>}
      {failed > 0 && <span className="batch-progress__seg batch-progress__seg--fail">{failed} failed</span>}
    </span>
  );
}

export function Batches() {
  const navigate = useNavigate();
  const [batches, setBatches] = useState<BatchSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    setError(null);
    api
      .getBatches()
      .then((r) => setBatches(r.batches))
      .catch(() => setError("Couldn’t load batches. Retry in a moment."));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const now = Date.now();

  return (
    <section className="batches">
      <div className="page-head">
        <div className="page-head__title">
          <h1>Batches</h1>
          <span className="page-head__count">
            {batches ? `${batches.length} batch${batches.length === 1 ? "" : "es"}` : ""}
          </span>
        </div>
        <Button variant="ghost" size="sm" icon="rotate" onClick={load}>
          Refresh
        </Button>
      </div>

      {error ? (
        <ErrorState title="Couldn’t load batches" onRetry={load} />
      ) : !batches ? (
        <div className="table-card">
          <SkeletonRows rows={5} cols={5} />
        </div>
      ) : batches.length === 0 ? (
        <EmptyState
          title="No batches yet"
          hint="Select orders in the queue and choose “Create batch” to group them for picking and packing."
        />
      ) : (
        <div className="table-card">
          <div className="table-scroll">
            <table className="q-table" aria-label="Batches">
              <thead>
                <tr>
                  <th>Batch</th>
                  <th>Mode</th>
                  <th className="num">Orders</th>
                  <th>Progress</th>
                  <th>Status</th>
                  <th className="num">Created</th>
                </tr>
              </thead>
              <tbody>
                {batches.map((b) => (
                  <tr key={b.id} className="q-row" tabIndex={0} onClick={() => navigate(`/batches/${b.id}`)}>
                    <td>
                      <span className="q-order">
                        <Icon name="layers" />
                        <span className="mono">{b.id}</span>
                      </span>
                      {b.note && <div className="batch-note truncate">{b.note}</div>}
                    </td>
                    <td>
                      <Badge title="Fulfilment mode">{modeLabel(b.mode)}</Badge>
                    </td>
                    <td className="num tnum">{b.orderCount}</td>
                    <td>
                      <BatchProgress b={b} />
                    </td>
                    <td>
                      <span className={`chip chip--${batchStatusTone(b.status)}`}>
                        <span className="chip__dot" aria-hidden="true" />
                        {batchStatusLabel(b.status)}
                      </span>
                    </td>
                    <td className="num q-age">{formatAge(b.createdAt, now)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </section>
  );
}
